import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import heroImage from '../../assets/images_of_victoria/IMG_2296.jpg';
import supportImage from '../../assets/images_of_victoria/IMG_2264 (1).jpg';

const resourceHighlights = [
  {
    label: 'Reading',
    description: 'Magazine issues, blog posts, and family stories can all live in one visible place.',
  },
  {
    label: 'Downloads',
    description: 'Brochures, bylaws, and key documents should be easy to find without searching through the site.',
  },
  {
    label: 'Reference',
    description: "Resources gives the informational side of Victoria's Place a calmer home that can grow over time.",
  },
];

const resourceSections = [
  {
    title: "Victoria's Place Magazine",
    description:
      'A place for issue covers, summaries, featured stories, and future reading or download links.',
    note: 'Ideal for seasonal issues, announcements, and mission-centered storytelling.',
  },
  {
    title: 'Blogs and community stories',
    description:
      'Updates, reflections, educational posts, and family-centered stories can live here as a readable archive.',
    note: 'This keeps new content visible without turning the navigation into a long list of pages.',
  },
  {
    title: 'Brochure, bylaws, and documents',
    description:
      'Important informational materials belong together so supporters and families can find them quickly.',
    note: 'This section can hold PDFs, brochures, organizational documents, and future downloads.',
  },
];

const contentTypes = [
  'Magazine issues and cover previews',
  'Family stories and community updates',
  'Brochures, bylaws, and downloads',
];

export default function Resources() {
  return (
    <div className="resources-page">
      <section className="resources-library-hero resources-bleed">
        <div className="resources-library-media">
          <img src={heroImage} alt="Victoria standing on the grass in a white dress" />
        </div>

        <motion.div
          className="resources-library-inner"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, ease: 'easeOut' }}
        >
          <div className="resources-library-copy">
            <span className="eyebrow resources-library-eyebrow">Resources</span>
            <p className="resources-library-kicker">Reading, documents, and updates in one organized place</p>
            <h1>A calmer home for the materials families and supporters may return to most.</h1>
            <p className="resources-library-lead">
              Resources brings together the magazine, stories, brochures, bylaws, and future downloads so the
              informational side of Victoria&apos;s Place feels clear, useful, and easy to revisit.
            </p>

            <div className="resources-library-actions">
              <Link to="/about-us" className="button-primary tap-target">
                Read about us
              </Link>
              <Link to="/contact" className="button-secondary tap-target">
                Ask a question
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      <div className="page-shell resources-shell">
        <motion.section
          className="resources-highlight-row"
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.24 }}
          transition={{ duration: 0.45, ease: 'easeOut' }}
        >
          {resourceHighlights.map((item) => (
            <article key={item.label} className="resources-highlight">
              <p className="home-section-label">{item.label}</p>
              <p>{item.description}</p>
            </article>
          ))}
        </motion.section>

        <section className="resources-story-grid">
          <motion.div
            className="resources-story-copy"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.24 }}
            transition={{ duration: 0.48, ease: 'easeOut' }}
          >
            <p className="home-section-label">What Belongs Here</p>
            <h2>This page should feel less like storage and more like a thoughtful library.</h2>
            <p>
              Families, supporters, and future partners should be able to come here for reading, downloads, and
              context without having to hunt through several parts of the site. The goal is clarity, not clutter.
            </p>

            <div className="resources-content-list">
              {contentTypes.map((item) => (
                <div key={item} className="resources-content-item">
                  <span />
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.figure
            className="resources-story-photo"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <img src={supportImage} alt="Victoria sitting outside with flowers and fairy wings" />
          </motion.figure>
        </section>

        <section className="resources-editorial-grid">
          {resourceSections.map((section, index) => (
            <motion.article
              key={section.title}
              className="resources-editorial-card"
              initial={{ opacity: 0, y: 22 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.16 }}
              transition={{ duration: 0.45, delay: index * 0.05, ease: 'easeOut' }}
            >
              <p className="info-kicker">Resource area</p>
              <h2>{section.title}</h2>
              <p>{section.description}</p>
              <p className="resources-editorial-note">{section.note}</p>
            </motion.article>
          ))}
        </section>

        <motion.section
          className="resources-final-band"
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.24 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <div className="resources-final-copy">
            <p className="home-section-label home-section-label--light">Build The Library</p>
            <h2>This section can grow steadily without making the site harder to navigate.</h2>
            <p>
              As magazine issues, blog content, brochures, and reference materials are added, this page can become the
              central home for the reading and document side of Victoria&apos;s Place.
            </p>
          </div>

          <div className="resources-final-actions">
            <Link to="/gallery" className="button-secondary tap-target">
              View the gallery
            </Link>
            <Link to="/contact" className="button-primary tap-target">
              Contact the team
            </Link>
          </div>
        </motion.section>
      </div>
    </div>
  );
}
